import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, X, Briefcase, DollarSign, UserPlus, MessageSquare } from 'lucide-react';
import * as api from '../lib/api';

const iconMap = { application: Briefcase, payment: DollarSign, message: MessageSquare, referral: UserPlus, job: Briefcase };
const colorMap = { application: ['bg-[#E0E8D0]','text-[#0A3900]'], payment: ['bg-green-100','text-green-600'], message: ['bg-purple-100','text-purple-600'], referral: ['bg-orange-100','text-orange-600'], job: ['bg-gray-100','text-gray-600'] };

const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, [user]);

  const loadNotifications = async () => {
    try {
      const data = await api.getNotifications();
      setNotifications(data.notifications || []);
    } catch {
      // fallback mock
      setNotifications([
        { notification_id: '1', type: 'application', title: 'Application Status Update', message: 'Your application for Senior AI Engineer has been reviewed', read: false, created_at: new Date().toISOString() },
        { notification_id: '2', type: 'payment', title: 'Payment Received', message: '$1,200 has been deposited to your account', read: false, created_at: new Date().toISOString() },
        { notification_id: '3', type: 'message', title: 'New Message', message: 'TechCorp AI sent you a message about your application', read: true, created_at: new Date().toISOString() },
        { notification_id: '4', type: 'referral', title: 'Referral Update', message: 'Your referral Sarah Johnson completed her first project. You earned $200!', read: true, created_at: new Date().toISOString() },
        { notification_id: '5', type: 'job', title: 'New Job Match', message: '5 new jobs match your profile', read: true, created_at: new Date().toISOString() },
      ]);
    } finally { setLoading(false); }
  };

  const markAsRead = async (id) => {
    try { await api.markNotificationRead(id); } catch {}
    setNotifications(notifications.map(n => n.notification_id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = async () => {
    try { await api.markAllNotificationsRead(); } catch {}
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const handleDelete = async (id) => {
    try { await api.deleteNotification(id); } catch {}
    setNotifications(notifications.filter(n => n.notification_id !== id));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Notifications</h1>
              <p className="text-gray-600">{unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}</p>
            </div>
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline"><Check className="w-4 h-4 mr-2" />Mark all as read</Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading notifications...</div>
          ) : (
            <div className="space-y-3">
              {notifications.map((n) => {
                const Icon = iconMap[n.type] || Bell;
                const [bgColor, txtColor] = colorMap[n.type] || ['bg-gray-100', 'text-gray-600'];
                return (
                  <Card key={n.notification_id} className={`p-6 ${!n.read ? 'border-[#B8C4AD] bg-[#F1F5E6]' : ''}`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-full ${bgColor}`}><Icon className={`w-5 h-5 ${txtColor}`} /></div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{n.title}</h3>
                          {!n.read && <Badge className="bg-[#0A3900] text-white">New</Badge>}
                        </div>
                        <p className="text-gray-700 mb-2">{n.message}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">{timeAgo(n.created_at)}</span>
                          <div className="flex gap-2">
                            {!n.read && <Button size="sm" variant="ghost" onClick={() => markAsRead(n.notification_id)}>Mark as read</Button>}
                            <Button size="sm" onClick={() => window.location.href = n.action_url || '/explore'}>View</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(n.notification_id)}><X className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <Card className="p-12 text-center">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No notifications</h3>
              <p className="text-gray-600">You're all caught up!</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
